
import React, { useState } from 'react';
import './IntegrationsPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Card from '../../components/shared/Card';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';

type IntegrationCategory = 'All' | 'Payments' | 'Cloud' | 'Dev' | 'Media' | 'AI';

const INTEGRATIONS = [
  // Payments
  { id: 1, name: 'Stripe', desc: 'Accept payments, subscriptions, and invoices', icon: '💳', category: 'Payments', status: 'connected', docs: 'stripe.com/docs', color: '#635BFF' },
  { id: 2, name: 'PayPal', desc: 'PayPal checkout and mass payouts', icon: '🅿', category: 'Payments', status: 'disconnected', docs: 'developer.paypal.com', color: '#003087' },
  { id: 3, name: 'Zelle', desc: 'Instant bank transfers via Zelle', icon: '⚡', category: 'Payments', status: 'disconnected', docs: 'zellepay.com', color: '#6B2D8B' },
  // Cloud
  { id: 4, name: 'Cloudflare', desc: 'CDN, DNS, edge workers and security', icon: '🌐', category: 'Cloud', status: 'connected', docs: 'developers.cloudflare.com', color: '#F48120' },
  { id: 5, name: 'AWS S3', desc: 'Object storage and file hosting', icon: '☁', category: 'Cloud', status: 'connected', docs: 'aws.amazon.com/s3', color: '#FF9900' },
  { id: 6, name: 'Vercel', desc: 'Deployment and serverless functions', icon: '▲', category: 'Cloud', status: 'disconnected', docs: 'vercel.com/docs', color: '#E5E7EB' },
  // Dev
  { id: 7, name: 'GitHub', desc: 'Code repositories and CI/CD workflows', icon: '🐙', category: 'Dev', status: 'connected', docs: 'docs.github.com', color: '#E5E7EB' },
  { id: 8, name: 'MCP Server', desc: 'Model Context Protocol — tool server', icon: '🔌', category: 'Dev', status: 'connected', docs: 'modelcontextprotocol.io', color: '#1282D6' },
  { id: 9, name: 'Linear', desc: 'Issue tracking and project management', icon: '📋', category: 'Dev', status: 'disconnected', docs: 'linear.app/docs', color: '#5E6AD2' },
  // Media
  { id: 10, name: 'Emby', desc: 'Personal media server and streaming', icon: '🎬', category: 'Media', status: 'connected', docs: 'emby.media', color: '#52B54B' },
  { id: 11, name: 'Cloudinary', desc: 'Image and video transformation CDN', icon: '🖼', category: 'Media', status: 'disconnected', docs: 'cloudinary.com/docs', color: '#3448C5' },
  // AI
  { id: 12, name: 'OpenAI', desc: 'GPT-4o, DALL·E 3, Whisper APIs', icon: '🤖', category: 'AI', status: 'connected', docs: 'platform.openai.com', color: '#10A37F' },
  { id: 13, name: 'Anthropic', desc: 'Claude 3.5 Sonnet and future models', icon: '🧠', category: 'AI', status: 'connected', docs: 'docs.anthropic.com', color: '#C09853' },
  { id: 14, name: 'Replicate', desc: 'Run open-source AI models via API', icon: '⚙', category: 'AI', status: 'disconnected', docs: 'replicate.com/docs', color: '#8B5CF6' },
];

export default function IntegrationsPage() {
  const [category, setCategory] = useState<IntegrationCategory>('All');
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<Record<number, boolean>>(
    Object.fromEntries(INTEGRATIONS.map(i => [i.id, i.status === 'connected']))
  );
  const { addToast } = useToast();

  const toggle = (id: number, name: string) => {
    const next = !statuses[id];
    setStatuses(s => ({ ...s, [id]: next }));
    addToast(next ? 'success' : 'info', `${name} ${next ? 'connected' : 'disconnected'}`);
  };

  const filtered = INTEGRATIONS.filter(i => {
    const matchCat = category === 'All' || i.category === category;
    const matchSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.desc.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const connectedCount = Object.values(statuses).filter(Boolean).length;

  return (
    <div className="integrations-page">
      <PageHeader
        title="Integrations"
        breadcrumb={['Ops', 'Integrations']}
        actions={
          <Badge variant="success">{connectedCount} Connected</Badge>
        }
      />

      <div className="integrations-stats">
        {[
          { label: 'Connected', value: connectedCount, color: 'var(--color-success)' },
          { label: 'Available', value: INTEGRATIONS.length - connectedCount, color: 'var(--color-muted)' },
          { label: 'Total', value: INTEGRATIONS.length, color: 'var(--color-ocean-500)' },
        ].map(s => (
          <div key={s.label} className="integration-stat-pill" style={{ borderColor: s.color + '33', color: s.color }}>
            <span className="integration-stat-pill__val">{s.value}</span>
            <span className="integration-stat-pill__label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="integrations-toolbar">
        <input
          className="integrations-search"
          placeholder="Search integrations…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="integrations-categories">
          {(['All', 'Payments', 'Cloud', 'Dev', 'Media', 'AI'] as IntegrationCategory[]).map(c => (
            <button
              key={c}
              className={`int-cat${category === c ? ' int-cat--active' : ''}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="integrations-grid">
        {filtered.map(i => {
          const isOn = statuses[i.id];
          return (
            <div key={i.id} className={`integration-card${isOn ? ' integration-card--on' : ''}`}>
              <div className="integration-card__left">
                <div className="integration-card__icon-wrap" style={{ background: i.color + '22', borderColor: i.color + '44' }}>
                  <span className="integration-card__icon">{i.icon}</span>
                </div>
                <div className="integration-card__info">
                  <div className="integration-card__name">
                    {i.name}
                    {isOn && <span className="integration-card__live">●</span>}
                  </div>
                  <div className="integration-card__desc">{i.desc}</div>
                  <div className="integration-card__category">
                    <Badge variant="default">{i.category}</Badge>
                  </div>
                </div>
              </div>
              <div className="integration-card__right">
                <button
                  className="integration-card__docs"
                  onClick={() => addToast('info', `Opening docs: ${i.docs}`)}
                  title="View docs"
                >
                  ↗
                </button>
                <label className="toggle">
                  <input type="checkbox" checked={isOn} onChange={() => toggle(i.id, i.name)} />
                  <span className="toggle__slider" />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
